import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { StorageService, SharedChangesService } from '@shared/services';
import { TranslateService } from '@shared/services/translate';
import { Translator } from '@shared/services/translate/translate.class';
import { Client } from '@shared/models/client/client.class';
import { Utilities } from '@shared/models/utilities/utilities.class';
import { ShopboxTabComponent } from '@shared/components/component-schema/shopbox-tab-component.class';
import {
  APIFacadeService,
  API_TYPES,
  ERROR_TYPES,
  NextInterface,
  ErrorInterface,
  CompleteInterface
} from '@shared/services/facades/api-facade.service';
import { IntercomService } from '../../../intercom';
import { forkJoin, filter } from 'rxjs';
import { WindowRef } from '@shared/global';
import { Location } from '@angular/common';
import { AuthContainerComponent } from '../../../../shared/components/v2/auth-container/auth-container.component';
import { ErrorToolbarComponent } from '../../../../shared/components/error-toolbar/error-toolbar.component';
import { LoginFormComponent } from '../../components/login-form/login-form.component';
import { Ripple } from 'primeng/ripple';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
    selector: 'shopbox-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    providers: [APIFacadeService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AuthContainerComponent, ErrorToolbarComponent, LoginFormComponent, Ripple, TranslatePipe]
})
export class LogInComponent extends ShopboxTabComponent implements OnInit, OnDestroy {
  progressValue: number;
  selectedLang: any = {};
  supportedLanguages: Object[] = [];
  anyErrors = false;
  anyAccepts = false;
  acceptValue = '';
  errorValue  = '';

  noClients = false;
  isTest = undefined;
  isApp = false;
  isNativeApp = false;
  nextURL = undefined;
  lang: string;
  form: any;
  disableForm: boolean = false;
  currentClient: Client;
  devMode: boolean = true;
  migrationCompleted: boolean = false;

  constructor(
    private apiSrv: APIFacadeService,
    private router: Router,
    private auth: StorageService,
    private windRef: WindowRef,
    private _changeDetectRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private changeSimple: SharedChangesService,
    private _intercom: IntercomService,
    public _translate: TranslateService,
    private _router: Router,
    private location: Location
    ) {
      super(_translate);
    this.getParams();
    this.watchRouter(router);
    this.subscribeToApi();

    if(!this._router.url.includes('rana')) {
      this.devMode = false;
    } else {
      this.devMode = true;
    }
    this.clearBrowserCache();
  }

  ngOnInit() {
    this.supportedLanguages = new Translator().supportedLanguages;
    const currentLang = this.auth.getLang();
    this.selectMenu(currentLang);
    this.refreshTrans();
  }

  submit(values: any) {
    this.form = values;
    this.saveBtn();
  }

  refreshTrans() {
    const translateChanges = this.translate.onLangChanged.subscribe(() => {
      this._changeDetectRef.markForCheck();
    });
    this.apiSrv.subscriptionList.add(translateChanges);
  }

  watchRouter(router: Router) {
    const routerChanges = router.events.
      pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((_event: NavigationEnd) => {
        this.getParams();
      });
      this.apiSrv.subscriptionList.add(routerChanges);
  }

  getParams() {
    const paramsSub: any = this.activatedRoute.snapshot.queryParams;
    const utility = new Utilities();
    const isTestParam = utility.getParameterCaseInsensitive(paramsSub, 'istest');
    const langParam = (utility.getParameterCaseInsensitive(paramsSub, 'lang') ||
      utility.getParameterCaseInsensitive(paramsSub, 'language'));
    const isAppParam = utility.getParameterCaseInsensitive(paramsSub, 'isapp');
    const isAlphaParam = utility.getParameterCaseInsensitive(paramsSub, 'isalpha');
    const isNativeParam = utility.getParameterCaseInsensitive(paramsSub, 'isnativeapp');
    const nextParam = utility.getParameterCaseInsensitive(paramsSub, 'next');
    const economicTokenParam = utility.getParameterCaseInsensitive(
      paramsSub,
      'economic-token'
    );
    const migrationCompletedParam = utility.getParameterCaseInsensitive(
      paramsSub,
      'migration-completed'
    );

    if (migrationCompletedParam) {
      this.auth.setMigrationCompleted(migrationCompletedParam);
    }

    this.migrationCompleted = (this.auth.getMigrationCompleted() === 'true') ? true : false;

    if (economicTokenParam) {
      this.auth.setEconomicTokenTemp(economicTokenParam);
    }
    if (isTestParam) {
      this.isTest = JSON.parse(isTestParam);
      this.auth.setTestTemp(this.isTest);
    }
    if (langParam) {
      if (this.translate.checkExistLang(langParam)) {
        this.auth.setLang(langParam);
        this.translate.setCurrentLang();
        this.selectMenu(langParam);
      } else if (langParam === 'dk') {
        const langParamDa = 'da';
        this.auth.setLang(langParamDa);
        this.translate.setCurrentLang();
        this.selectMenu(langParamDa);
      }
    }
    if (isAppParam) {
      this.isApp = JSON.parse(isAppParam);
      this.auth.setIsAppTemp(String(this.isApp));
    }
    if (isAlphaParam) {
      this.auth.setIsAlphaTemp(String(isAlphaParam));
    }
    if (isNativeParam) {
      this.isNativeApp = JSON.parse(isNativeParam);
      this.auth.setIsNativeAppTemp(String(isNativeParam));
    }
    if (nextParam) {
      this.nextURL = nextParam;
    }
  }

  subscribeToApi() {
    this.apiSrv.subscribeToApi(this._changeDetectRef);
    this.apiSrv.nextEmit.subscribe((res: NextInterface) => {
      this.nextFn(res.res, res.apiType);
    });
    this.apiSrv.errorEmit.subscribe((res: ErrorInterface) => {
      this.errorFn(res.error, res.errorType);
    });
    this.apiSrv.completeEmit.subscribe((res: CompleteInterface) => {
      this.completeFn(res.apiType);
    });
  }

  save() {
    this.lang = this.selectedLang;
    const model = this.form;
    model.password = model.password ? model.password.trim() : model.password;
    this.loginFn(model);
  }

  loginFn(model: any) {
    this.disableForm = true;
    const accessRequest = this.apiSrv.shopboxApiService.accountService.login(model);
    this.apiSrv.apiType = API_TYPES.LOGIN;
    this.apiSrv.errorType = ERROR_TYPES.NORMAL;
    this.apiSrv.apiSubject.next(accessRequest);
  }

  getLoginCoreData() {
    const checkAccessRequest   = this.apiSrv.shopboxApiService.clientService.checkAccess();
    const getCurrencyRequest = this.apiSrv.shopboxApiService.countryService.getCountries();
    const getLoginCoreSub = forkJoin(checkAccessRequest, getCurrencyRequest);
    this.apiSrv.apiType = API_TYPES.GET_LOGIN_CORE_DATA;
    this.apiSrv.errorType = ERROR_TYPES.NORMAL;
    this.apiSrv.apiSubject.next(getLoginCoreSub);
  }

  cleanUpStorage() {
    this.auth.cleanUpStorage();
  }

  /**
   * getClient0
   * @function
   * @description [Check for indexes]
   * @param {*} data
   * @param {number} index
   * @returns {number}
   * @memberof LogInComponent
   */
  getClient0(data: any, index: number): number {
    let result = 0;
    let found = false;
    if (data[index].client0) {
      result = index;
      found = true;
    } else if (!found) {
      index++;
      result = this.getClient0(data, index);
    }
    return result;
  }

  saveBtn() {
    if (!this.activeProgress) {
      this.save();
    }
  }

  goForgot() {
    this.router.navigateByUrl('reset');
  }

  goRegist() {
    if(this.devMode) {
      this.router.navigateByUrl('signup');
    }
    if(!this.devMode) {
      const url = 'https://shopbox.com/registration/';
      this.windRef.nativeWindow.open(url, '_parent');
    }
  }

  /**
   * goCreateCompany
   * @function
   * @description Trigger when first time to create Account
   * @param {number} id id of current account
   * @memberof LogInComponent
   */
  goCreateCompany(): void {
    this.router.navigateByUrl(`/create-account`);
  }

  goSubscriptionPlans(): void {
    const plan = this.currentClient['subscriptionData'];
    const subscriptions = this.currentClient['subscriptions'];
    const isTrialClient = this.currentClient['isTrial'];
    const isSubscriptionEnabled = this.currentClient['subscriptionEnable'];
    if ((isSubscriptionEnabled && (!plan && !isTrialClient)) || ((!Array.isArray(subscriptions) || !subscriptions.length))) {
      if((isSubscriptionEnabled && (!plan && !isTrialClient))) {
        this.auth.setFirstTimeCreation(true);
      }
      this.router.navigateByUrl('/create-account');
    } else {
      if (this.nextURL) {
        const url = this.nextURL.split('?');
        let search: any = {};
        if (url[1]) {
          search = url[1];
          search = JSON.parse('{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
        }
        this.router.navigate([url[0]], { queryParams: search });
      } else {
        this.router.navigate(['dashboard']);
      }
    }
  }

  /**
   * selectMenu
   * @function
   * @description [use to Select language from Menu]
   * @param {Object} lang [language used]
   * @return translate
   */
  selectMenu(lang: any) {
    this.selectedLang = lang;
    this.selectLang(lang);
    this.toggleMenu(lang);
    this._changeDetectRef.markForCheck();
  }

  /**
   * toggleMenu
   * @function
   * @description [ set disabled to language]
   * @param {String} code [code to language]
   */
  private toggleMenu(code: string) {
    this.supportedLanguages = new Translator().disabledLanguages(code);
  }

  private selectLang(lang: string) {
    this.translate.use(lang);
  }

  trackItemFn(index: number, item: any) {
    return index;
  }

  nextFn(res: any, type: API_TYPES) {
    switch(type) {
      case API_TYPES.LOGIN:
        this.setLoginInfo(res);
        break;
      case API_TYPES.GET_LOGIN_CORE_DATA:
        this.setAccessInfo(res[0]);
        this.setCountryInfo(res[1]);
        break;
    }
  }

  errorFn(error: any, type: ERROR_TYPES) {
    this.cleanUpStorage();
    this.anyErrors = true;
    this.errorValue = (typeof error) === 'string' ? error : error['message'];
    this.disableForm = false;
    if (this.nextURL) {
      this.router.navigate(['login'], {
        queryParams: {
          next: this.nextURL
        }
      });
    }
    this.changeProgress(false, this._changeDetectRef);
   }

  completeFn(type: API_TYPES) {
    switch(type) {
      case API_TYPES.LOGIN:
        this.getLoginCoreData();
        break;
      case API_TYPES.GET_LOGIN_CORE_DATA:
        this.completeCurrency();
        this.changeProgress(false, this._changeDetectRef);
        break;
      default:
        // this.changeProgress(false, this._changeDetectRef);
        break;
    }
  }
  setAccessInfo(res: any) {
    const model = this.form;
    this.noClients = res.data.length === 0 ? true : false;
    if (this.noClients) {
      this.changeProgress(false, this._changeDetectRef);
      this.auth.updateIntercomClient(this._intercom);
      this.goCreateCompany();
    } else {
      const role = res.data[0].role;
      this.auth.setPermissionTemp(role);
      if (this.isTest !== undefined) {
        this.auth.setTestTemp(this.isTest);
      }
      // if (model.remember_me) {
      //   this.auth.setPermission(role);
      //   if (this.isTest !== undefined) {
      //     this.auth.setTest(this.isTest);
      //   }
      // } else {
      //   this.auth.setPermissionTemp(role);
      //   if (this.isTest !== undefined) {
      //     this.auth.setTestTemp(this.isTest);
      //   }
      // }
      const index = this.getClient0(res.data, 0);
      const client = res.data[index].client0;
      const info = new Client(client);
      info['hideRefund'] = res.data[index].hide_refund;
      info['role'] = res.data[index].role;
      info['branch'] = res.data[index].branch;
      this.currentClient = info;
      this.auth.setTakeAwayActivation(!!info.enableTakeAwayPrice);
      // if (model.remember_me) {
      //   this.auth.setClient(String(info.clientId));
      //   this.auth.setCurrentClient(info);
      // } else {
      //   this.auth.setClientTemp(String(info.clientId));
      //   this.auth.setCurrentClientTemp(info);
      // }
      this.auth.setClientTemp(String(info.clientId));
      this.auth.setCurrentClientTemp(info);
      this.auth.updateIntercomClient(this._intercom);
    }
  }

  setCountryInfo(res: any) {
    const model = this.form;
    const currency = this.apiSrv.shopboxApiService.accountService.getCurrencyId();
    if (res.data && res.data.length > 0) {
      const found = res.data.findIndex((element) => element.uid === currency);
      if (found !== -1) {
        this.currentCurrency = res.data[found]['cn_currency_iso_3'];
        // if (model.remember_me) {
        //   this.auth.setCurrency(this.currentCurrency);
        // } else {
        //   this.auth.setCurrencyTemp(this.currentCurrency);
        // }
        this.auth.setCurrencyTemp(this.currentCurrency);
        this.changeSimple.sharedCurrencyChanges(this.currentCurrency);
      } else {
        this.auth.deleteCurrentClient();
      }
    }
  }

  completeCurrency() {
    if (this.noClients) {
      this.goCreateCompany();
    } else {
      this.anyErrors = false;
      this.goSubscriptionPlans();
    }
    this.setSettingsOfInterface();
  }

  setLoginInfo(res: any) {
    const data = res;
    const model = this.form;
    data['account']['crdate'] = res['crdate'];
    this.auth.setTokenTemp(data['accessToken']);
    this.auth.setAccountTemp(data['account']);
    if (this.isTest !== undefined) {
      this.auth.setTestTemp(this.isTest);
    }
    // if (model.remember_me) {
    //   this.auth.setToken(data['accessToken']);
    //   this.auth.setAccount(data['account']);
    //   if (this.isTest !== undefined) {
    //     this.auth.setTest(this.isTest);
    //   }
    // } else {
    //   this.auth.setTokenTemp(data['accessToken']);
    //   this.auth.setAccountTemp(data['account']);
    //   if (this.isTest !== undefined) {
    //     this.auth.setTestTemp(this.isTest);
    //   }
    // }
  }

  setSettingsOfInterface() {
    const model = this.form;
    if (this.isTest !== undefined) {
      this.auth.setTestTemp(this.isTest);
    }
    // if (model.remember_me) {
    //   if (this.isTest !== undefined) {
    //     this.auth.setTest(this.isTest);
    //   }
    // } else {
    //   if (this.isTest !== undefined) {
    //     this.auth.setTestTemp(this.isTest);
    //   }
    // }
  }

  getSelectedLang(): string {
    const selectedLang = this.supportedLanguages.find(element => element['disabled']);
    return selectedLang ? selectedLang['text'] : '';
  }

  ngOnDestroy() {
    this.apiSrv.destroy();
  }

  private clearBrowserCache(): void {
    // Force reload if coming from cache
    if (this.windRef.nativeWindow.performance
        && this.windRef.nativeWindow.performance.navigation.type
        === this.windRef.nativeWindow.performance.navigation.TYPE_BACK_FORWARD) {
      this.windRef.nativeWindow.location.reload();
    }
  }
}
